import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import { Add, FileCopy, Search } from '@material-ui/icons';
import { toast } from 'react-toastify';

import { listBatches, duplicateBatch } from '../../../../services/certificateBatches';
import AdminMenu from '../../../../components/Admin/MenuDetalhes/AdminMenu';
import {
  MainContainer,
  ContentContainer,
  HeaderRow,
  TitleWrapper,
  Title,
  Subtitle,
  SearchWrapper,
  CompanyCell,
  CompanyLogo,
  ClickableRow,
  RowTitle,
  ErrorsHint,
  StatusBadge,
  EmptyState,
  SkeletonBar,
  SearchIcon,
  StyledTableContainer,
} from './styles';

const STATUS_LABEL = {
  draft: 'Rascunho',
  processing: 'Processando…',
  done: 'Concluída',
  partial_error: 'Com erros',
};

const STATUS_STYLE = {
  draft: { backgroundColor: '#eee', color: '#616161' },
  processing: { backgroundColor: '#FFF3E0', color: '#E65100' },
  done: { backgroundColor: '#E8F5E9', color: '#2E7D32' },
  partial_error: { backgroundColor: '#FFEBEE', color: '#C62828' },
};

const SKELETON_ROWS = 5;

export default function CertificateBatchesList() {
  const history = useHistory();
  const [batches, setBatches] = useState([]);
  const [count, setCount] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [duplicatingId, setDuplicatingId] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await listBatches({ search: debouncedSearch });
      setBatches(data.rows || []);
      setCount(data.count || 0);
    } catch (error) {
      toast.error('Houve um erro ao carregar as turmas');
    } finally {
      setIsLoading(false);
    }
  }, [debouncedSearch]);

  useEffect(() => {
    load();
  }, [load]);

  const handleDuplicate = async (event, id) => {
    event.stopPropagation();
    setDuplicatingId(id);
    try {
      const { data } = await duplicateBatch(id);
      toast.success('Turma duplicada com sucesso');
      setDuplicatingId(null);
      history.push(`/admin/certificados/turmas/${data.id}`);
    } catch (error) {
      toast.error('Houve um erro ao duplicar a turma');
      setDuplicatingId(null);
    }
  };

  const goToBatch = (id) => history.push(`/admin/certificados/turmas/${id}`);
  const goToWizard = () => history.push('/admin/certificados/turmas/nova');

  return (
    <MainContainer>
      <AdminMenu />
      <ContentContainer>
        <HeaderRow>
          <TitleWrapper>
            <Title>Certificados In-Company</Title>
            {!isLoading && <Subtitle>{count} turma{count === 1 ? '' : 's'} encontrada{count === 1 ? '' : 's'}</Subtitle>}
          </TitleWrapper>
          <Button variant="contained" color="primary" startIcon={<Add />} onClick={goToWizard}>
            Nova turma
          </Button>
        </HeaderRow>

        <SearchWrapper>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Buscar turma"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <SearchIcon>
                  <Search fontSize="small" />
                </SearchIcon>
              ),
            }}
          />
        </SearchWrapper>

        <StyledTableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Turma</TableCell>
                <TableCell>Empresa</TableCell>
                <TableCell>Curso</TableCell>
                <TableCell>Alunos</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Criada em</TableCell>
                <TableCell />
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading &&
                Array(SKELETON_ROWS)
                  .fill(0)
                  .map((_, index) => (
                    <TableRow key={index}>
                      <TableCell colSpan={7}>
                        <SkeletonBar />
                      </TableCell>
                    </TableRow>
                  ))}

              {!isLoading && batches.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7}>
                    <EmptyState>Nenhuma turma encontrada.</EmptyState>
                  </TableCell>
                </TableRow>
              )}

              {!isLoading &&
                batches.map((b) => {
                  const students = b.students || [];
                  const generated = students.filter((s) => s.status === 'generated').length;
                  const errors = students.filter((s) => s.status === 'error').length;

                  return (
                    <ClickableRow key={b.id} onClick={() => goToBatch(b.id)}>
                      <TableCell>
                        <RowTitle>{b.title}</RowTitle>
                      </TableCell>
                      <TableCell>
                        <CompanyCell>
                          {b.company?.logo_url && (
                            <CompanyLogo src={b.company.logo_url} alt={b.company?.name || ''} />
                          )}
                          {b.company?.name}
                        </CompanyCell>
                      </TableCell>
                      <TableCell>{b.id_curso}</TableCell>
                      <TableCell>
                        {generated}/{students.length}
                        {errors > 0 && <ErrorsHint>({errors} erros)</ErrorsHint>}
                      </TableCell>
                      <TableCell>
                        <StatusBadge
                          statusColor={STATUS_STYLE[b.status]?.color}
                          statusBg={STATUS_STYLE[b.status]?.backgroundColor}
                        >
                          {STATUS_LABEL[b.status] || b.status}
                        </StatusBadge>
                      </TableCell>
                      <TableCell>
                        {b.createdAt ? new Date(b.createdAt).toLocaleDateString('pt-BR') : '-'}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          title="Duplicar turma"
                          disabled={duplicatingId === b.id}
                          onClick={(event) => handleDuplicate(event, b.id)}
                        >
                          {duplicatingId === b.id ? (
                            <CircularProgress size={20} />
                          ) : (
                            <FileCopy fontSize="small" />
                          )}
                        </IconButton>
                      </TableCell>
                    </ClickableRow>
                  );
                })}
            </TableBody>
          </Table>
        </StyledTableContainer>
      </ContentContainer>
    </MainContainer>
  );
}
